/* eslint-disable camelcase, no-shadow, max-len */
import { useCallback, useEffect, useRef, useState } from 'react'
import { string, object, bool, func, array } from 'prop-types'
import { connect } from 'react-redux'
import classNames from 'classnames'
import styled from 'styled-components'
import { withRouter } from 'next/router'

import {
  getSiteLanguage,
  logger,
  shallowCompare
} from '@utils'
import IntersectionCapture from '@reusable-modules/IntersectionCapture'
import cssClasses from '@constants/cssClasses'
import { setEventsFilters } from '@stores/events'
import { Text, Separator } from '@components'
import { Events as EventsService } from '@services'

import BrowseFilters from './BrowseFilters'
import BrowseEvents from './BrowseEvents'
import { useLabelsFromAPI } from '@dmi-mch/hooks'
import { Container, Row, Spinner } from '@mch-group/uikit-components'
import DayCalendarStyles from './styles/DayCalendarStyles'

const mID = 'otm-browse-events'

const OtmBrowseEvents = ({
  className,
  router,
  isEventsModule = true,
  availableFilters = ['location', 'type', 'date', 'category', 'sort'],
  defaultRequestFilters = {},
  eventsLabels,
  eventsFilters,
  isLoggedIn,
  aev_grp1_msg_keep_rsvp_updated,
  aev_grp1_msg_no_rsvp,
  setEventsFilters: setEventsFiltersInStore,
  aevs_grp1_location_filter_visibility
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [eventsList, setEventsList] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [eventsFiltersData, setEventsFiltersData] = useState({})

  const eventsOffset = useRef(0)
  const prevQuery = useRef({})
  const currentFilters = useRef({})

  const { labels: { Events: browseLabels = {} } = {} } = useLabelsFromAPI(['Events'], getSiteLanguage())

  const fetchEvents = useCallback(
    async (filters = {}, loadMore = false, resetOffset = false) => {
      if (loadMore) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
      }

      if (resetOffset && eventsOffset.current !== 0) {
        eventsOffset.current = 0
      }

      hasMore && setHasMore(false)

      try {
        const response = await EventsService.getUpcomingEvents({
          limit: 36,
          offset: eventsOffset.current,
          locale: getSiteLanguage(),
          online: false,
          ...(router.asPath === '/events/attending' ? { attending: true, includeFilters: false } : {}),
          ...defaultRequestFilters,
          ...filters
        })

        // ok
        if (response.data && response.ok) {
          //@ts-ignore
          const { items, hasMore, limit } = response.data.events
          //@ts-ignore
          setEventsList(list => (loadMore ? [...list, ...items] : [...items]))
          setHasMore(hasMore)

          if (
            !loadMore &&
            router.asPath !== '/events/attending' &&
            //@ts-ignore
            response.data.filters && response.data.filters.length > 0
          ) {
            //@ts-ignore
            setEventsFiltersData(response.data.filters.reduce((sum, current) => ({ ...sum, [current.entityType]: current.options }), {}))
          }

          if (hasMore) {
            eventsOffset.current += limit
          }
        }
      } catch (e) {
        logger(e)
      }

      if (loadMore) {
        setIsLoadingMore(false)
      } else {
        setIsLoading(false)
      }

    },
    [hasMore, router.asPath, defaultRequestFilters])

  const loadMore = useCallback(() => {
    fetchEvents(false, currentFilters.current, true)
  }, [fetchEvents])

  const onFilterChange = useCallback(
    (params) => {
      if (isEventsModule) {
        const existingParams = router.query
        const updatedParams = { ...existingParams, ...params }
        router.push({
          pathname: '/',
          query: {
            orderBy: updatedParams.orderBy || eventsFilters.orderBy,
            date: updatedParams.date,
            startDate: updatedParams.startDate,
            endDate: updatedParams.endDate,
            activeTab: updatedParams.activeTab
          }
        }, `${router.asPath}`,
          { shallow: true })
      } else {
        setEventsFiltersInStore(params, isLoggedIn, true)
      }
    },
    [isEventsModule, isLoggedIn, router, setEventsFiltersInStore, eventsFilters]
  )

  useEffect(() => {
    if (!shallowCompare(prevQuery.current, router.query)) {
      const filters = setEventsFiltersInStore(router.query, isLoggedIn)
      fetchEvents(filters, false, true)
      prevQuery.current = { ...router.query }
      currentFilters.current = { ...filters }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query])

  useEffect(() => {
    if (!eventsList) {
      fetchEvents()
    }
  }, [eventsList, fetchEvents])
  if (!browseLabels) return null
  return (
    <section className={classNames(className, cssClasses.CMS_MODULE)}>
      <Container className={`${mID}${eventsList.length === 0 ? '__no-result' : ''}`}>
        {router.asPath === '/events/attending' ? (
          <>
            <Text isHtml className={cssClasses.CMS_MODULE}>
              {eventsList && eventsList.length > 0 ? aev_grp1_msg_keep_rsvp_updated : aev_grp1_msg_no_rsvp}
            </Text>
            <Separator isRow />
          </>
        ) : (
          //@ts-ignore
          <BrowseFilters
            eventsLabels={eventsLabels}
            eventsFilters={eventsFilters}
            eventsFiltersData={eventsFiltersData}
            isLoggedIn={isLoggedIn}
            locationLabel={browseLabels.browseFilterLocation}
            typeLabel={browseLabels.browseFilterType}
            dayLabel={browseLabels.browseFilterDay}
            categoryLabel={browseLabels.browseFilterCategory}
            sortByLabel={browseLabels.browseFilterSortBy}
            allLabel={browseLabels.browseFilterAll}
            allEventsLabel={browseLabels.browseFilterAllEvents}
            allVirtualEventsLabel={browseLabels.allVirtualEventsFilterLabel}
            allExhibitionsLabel={browseLabels.browseFilterAllExhibitions}
            endDateLabel={browseLabels.browseFilterEndDate}
            startDateLabel={browseLabels.browseFilterStartDate}
            recentlyAddedLabel={browseLabels.browseFilterRecentlyAdded}
            moreLocationsLabel={browseLabels.browseFilterMoreLocations}
            selectedNumberLabel={browseLabels.browseFilterSelected}
            availableFilters={availableFilters}
            onFilterChange={onFilterChange}
            locationVisibility={aevs_grp1_location_filter_visibility}
          />
        )}
        {eventsList && isLoading && (
          <Row className={`${mID}__loading`} padded='horizontally' centered>
            Loading...
          </Row>
        )}
        {/* @ts-ignore */}
        <BrowseEvents eventsList={eventsList} noEventsLabel={eventsLabels.las_grp1_noevents} slug={router.asPath} />

        {/* @ts-ignore */}
        <IntersectionCapture onIntersect={loadMore} active={!isLoadingMore && hasMore} />

        {isLoadingMore && hasMore && <Spinner />}
      </Container>
      <DayCalendarStyles />
    </section>
  )
}

OtmBrowseEvents.propTypes = {
  className: string,
  router: object,
  isEventsModule: bool,
  availableFilters: array,
  defaultRequestFilters: object,
  eventsLabels: object,
  browseLabels: object,
  eventsFilters: object,
  isLoggedIn: bool,
  setEventsFilters: func,
  setEventsFiltersData: func,
  aev_grp1_msg_keep_rsvp_updated: string,
  aev_grp1_msg_no_rsvp: string,
  aevs_grp1_location_filter_visibility: string
}

const ConnectedOtmBrowseEvents = connect(
  //@ts-ignore
  ({ user, shared, events: { eventsFilters } }) => ({
    isLoggedIn: Boolean(user.me),
    //@ts-ignore
    eventsLabels: shared.globalModules.labelModule.labels || {},
    eventsFilters
  }),
  {
    setEventsFilters
  }
)(withRouter(OtmBrowseEvents))

export default styled(ConnectedOtmBrowseEvents)`
  .${mID} {
    &__column {
      margin-bottom: 64px !important;

      @media (max-width: 992px) {
        margin-bottom: 40px !important;
      }
    }

    &__loading {
      margin-bottom: 40px;
    }

    &__load-more {
      margin: 100px auto;
    }

    &__no-result {
      min-height: 455px;
    }
  }
`
